import { z } from "zod";
import type { BusinessFull } from "./business.server";

// ── Domain Schema (DB / server truth) ─────────────────────────────────────────

export const BusinessSchema = z.object({
  companyName: z.string().trim().min(1).max(200),

  legalName: z.string().trim().max(200).nullable(),
  companyType: z.string().trim().max(100).nullable(),
  tin: z.string().trim().max(50).nullable(),
  industry: z.string().trim().max(100).nullable(),
  phone: z.string().trim().max(50).nullable(),
  fax: z.string().trim().max(50).nullable(),

  email: z.string().email().max(200).nullable(),
  website: z.string().url().max(500).nullable(),

  address: z.object({
    line1: z.string().trim().max(200).nullable(),
    line2: z.string().trim().max(200).nullable(),
    city: z.string().trim().max(100).nullable(),
    state: z.string().trim().max(100).nullable(),
    postalCode: z.string().trim().max(20).nullable(),
    country: z.string().trim().max(100).nullable(),
  }),
});

// Derived types and schemas — zero duplication.
export type Business = z.infer<typeof BusinessSchema>;

export const CreateBusinessSchema = BusinessSchema.pick({
  companyName: true,
  industry: true,
}).extend({
  // Optional industry template slug. When provided, the matching template's
  // chart of accounts is seeded into the new business on creation.
  industryTemplateId: z.string().trim().max(100).optional(),
});

export const UpdateBusinessSchema = BusinessSchema.partial().required({
  companyName: true,
});

export type UpdateBusinessInput = z.infer<typeof UpdateBusinessSchema>;
export type CreateBusinessInput = z.infer<typeof CreateBusinessSchema>;

// ── Form Schema (UI layer) ────────────────────────────────────────────────────

export const BusinessFormSchema = z.object({
  companyName: z
    .string()
    .min(1, "Company name is required")
    .max(200, "Must be 200 characters or less"),

  legalName: z.string().max(200, "Must be 200 characters or less"),
  companyType: z.string().max(100, "Must be 100 characters or less"),
  tin: z.string().max(50, "Must be 50 characters or less"),
  industry: z.string().max(100, "Must be 100 characters or less"),
  phone: z.string().max(50, "Must be 50 characters or less"),
  fax: z.string().max(50, "Must be 50 characters or less"),

  email: z
    .string()
    .email("Invalid email")
    .max(200, "Must be 200 characters or less")
    .or(z.literal("")),
  website: z
    .string()
    .url("Please enter a valid URL (e.g. https://example.com)")
    .max(500, "Must be 500 characters or less")
    .or(z.literal("")),

  address: z.object({
    line1: z.string().max(200, "Must be 200 characters or less"),
    line2: z.string().max(200, "Must be 200 characters or less"),
    city: z.string().max(100, "Must be 100 characters or less"),
    state: z.string().max(100, "Must be 100 characters or less"),
    postalCode: z.string().max(20, "Must be 20 characters or less"),
    country: z.string().max(100, "Must be 100 characters or less"),
  }),
});

export type BusinessForm = z.infer<typeof BusinessFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

const emptyToNull = (v: string): string | null =>
  v.trim() === "" ? null : v.trim();

export function formToDomain(input: BusinessForm): Business {
  return {
    companyName: input.companyName.trim(),

    legalName: emptyToNull(input.legalName),
    companyType: emptyToNull(input.companyType),
    tin: emptyToNull(input.tin),
    industry: emptyToNull(input.industry),
    phone: emptyToNull(input.phone),
    fax: emptyToNull(input.fax),

    email: input.email === "" ? null : input.email,
    website: input.website === "" ? null : input.website,

    address: {
      line1: emptyToNull(input.address.line1),
      line2: emptyToNull(input.address.line2),
      city: emptyToNull(input.address.city),
      state: emptyToNull(input.address.state),
      postalCode: emptyToNull(input.address.postalCode),
      country: emptyToNull(input.address.country),
    },
  };
}

// ── Mapping: Domain → Form ────────────────────────────────────────────────────

const nullToEmpty = (v: string | null | undefined): string => v ?? "";

export function domainToForm(input: BusinessFull): BusinessForm {
  return {
    companyName: input.companyName ?? "",

    legalName: nullToEmpty(input.legalName),
    companyType: nullToEmpty(input.companyType),
    tin: nullToEmpty(input.tin),
    industry: nullToEmpty(input.industry),
    phone: nullToEmpty(input.phone),
    fax: nullToEmpty(input.fax),

    email: nullToEmpty(input.email),
    website: nullToEmpty(input.website),

    address: {
      line1: nullToEmpty(input.address?.line1),
      line2: nullToEmpty(input.address?.line2),
      city: nullToEmpty(input.address?.city),
      state: nullToEmpty(input.address?.state),
      postalCode: nullToEmpty(input.address?.postalCode),
      country: nullToEmpty(input.address?.country),
    },
  };
}
